import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import {
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import html2pdf from 'html2pdf.js';
import { trackInteraction, startTimeTracking, stopTimeTracking, trackSectionViews } from '../utils/interactionTracking';
import { useAnalytics } from '../hooks/useAnalytics';

const Watermark = ({ businessData }) => (
  <div className="flex items-center justify-center py-3 mb-4 bg-gray-50 rounded-lg" style={{ borderBottom: `1px solid ${THEME.primary}20` }}>
    <span className="text-sm" style={{ color: THEME.subtext }}>Invoice created through</span>
    <img src="/logo.png" alt="Aided Logo" className="h-6 w-auto mr-2" />
  </div>
);

export default function PublicInvoice() {
  const { id } = useParams();
  const { logEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicInvoice, setPublicInvoice] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [isPremium, setIsPremium] = useState(true);
  const [sectionObserver, setSectionObserver] = useState(null);

  useEffect(() => {
    fetchPublicInvoice();

    // Track page view
    if (id) {
      trackInteraction(id, 'view', 'invoice');
      startTimeTracking(id, 'invoice');
      
      // Setup section tracking after a short delay to ensure DOM is ready
      setTimeout(() => {
        const observer = trackSectionViews(id, 'invoice');
        setSectionObserver(observer);
      }, 1000);
    }

    return () => {
      stopTimeTracking();
      if (sectionObserver) {
        sectionObserver.disconnect();
      }
    };
  }, [id]);

  useEffect(() => {
    if (id) {
      logEvent('PublicInvoice', 'View Public Invoice', `Invoice ID: ${id}`);
    }
  }, [id]);

  const fetchPublicInvoice = async () => {
    try {
      const { data: publicInvoiceData, error: publicInvoiceError } = await supabase
        .from('public_invoices')
        .select('*')
        .eq('id', id)
        .single();

      if (publicInvoiceError) throw publicInvoiceError;
      setPublicInvoice(publicInvoiceData);

      // Fetch invoice details
      const { data: invoiceData, error: invoiceError } = await 
      supabase.rpc('get_public_invoice', { invoice_unique_id: id });
      
      if (invoiceError) throw invoiceError;
      setInvoiceData(invoiceData[0].invoice_data);
      setClientData(invoiceData[0].client_data);
      setInvoiceItems(invoiceData[0].invoice_items);
      setBusinessData(invoiceData[0].business_data);

      // Check subscription status
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('plan_type')
        .eq('user_id', invoiceData[0].invoice_data.user_id)
        .single();

      if (!subscriptionError && subscriptionData) {
        setIsPremium(subscriptionData.plan_type.toLowerCase() === 'paid');
      } else {
        setIsPremium(false);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (paymentData) => {
    try {
      logEvent('PublicInvoice', 'Payment Attempt', `Invoice ID: ${id}, Amount: ${paymentData.amount}`);
      // ... existing payment logic ...
      logEvent('PublicInvoice', 'Payment Success', `Invoice ID: ${id}`);
    } catch (error) {
      logEvent('PublicInvoice', 'Payment Error', error.message);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      logEvent('PublicInvoice', 'Download PDF', `Invoice ID: ${id}`);
      // Track download event
      await trackInteraction(id, 'pdf_download', 'invoice');
      
      const element = document.getElementById('invoice-content');
      const opt = {
        margin: 1,
        filename: `invoice-${invoiceData.invoice_number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      html2pdf().set(opt).from(element).save();
      logEvent('PublicInvoice', 'Download PDF Success', `Invoice ID: ${id}`);
    } catch (error) {
      logEvent('PublicInvoice', 'Download PDF Error', error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return THEME.success;
      case 'overdue':
        return THEME.error;
      case 'sent':
        return THEME.warning;
      default:
        return THEME.subtext;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: THEME.primary }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: THEME.primary }}>
        <div className="bg-white rounded-xl p-8 text-center max-w-md mx-auto" style={{ boxShadow: shadows.lg }}>
          <h1 className="text-2xl font-bold mb-2" style={{ color: THEME.error }}>
            Invoice Not Found
          </h1>
          <p style={{ color: THEME.subtext }}>
            The invoice you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const { customization_options } = publicInvoice;
  const showPrices = customization_options?.showPrices ?? true;
  const showLineItems = customization_options?.showLineItems ?? true;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Download Button */}
        <div className="mt-6 flex justify-end" data-section-name="download-button">
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200"
            style={{ 
              background: THEME.background,
              boxShadow: shadows.md,
              color: THEME.primary,
            }}
          >
            <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
            Download Invoice
          </button>
        </div>

        {/* Main Invoice Content */}
        <div id="invoice-content" className="space-y-6">
          {/* Header Section */}
          <div className="bg-white rounded-xl p-6" data-section-name="header" style={{ boxShadow: shadows.lg }}>
            {!isPremium && <Watermark businessData={businessData} />}
            <div className="flex justify-between items-start">
              {/* Logo and Business Info */}
              <div className="flex-1">
                {businessData.logo_url && (
                  <img
                    src={businessData.logo_url}
                    alt="Business Logo"
                    className="h-16 w-auto object-contain mb-4"
                  />
                )}
                <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>
                  {businessData.business_name}
                </h1>
                {businessData.business_address && (
                  <p className="mt-1" style={{ color: THEME.subtext }}>
                    {businessData.business_address}
                  </p>
                )}
                {businessData.business_phone && (
                  <p className="mt-1" style={{ color: THEME.subtext }}>
                    {businessData.business_phone}
                  </p>
                )}
                {businessData.business_email && (
                  <p className="mt-1" style={{ color: THEME.subtext }}>
                    {businessData.business_email}
                  </p>
                )}
              </div>

              {/* Invoice Details */}
              <div className="text-right">
                <h2 className="text-2xl font-bold mb-2" style={{ color: THEME.text }}>
                  Invoice #{invoiceData.invoice_number}
                </h2>
                <p className="text-sm" style={{ color: THEME.subtext }}>
                  Date: {new Date(invoiceData.invoice_date).toLocaleDateString()}
                </p>
                <p className="text-sm" style={{ color: THEME.subtext }}>
                  Due Date: {new Date(invoiceData.due_date).toLocaleDateString()}
                </p>
                <div className="mt-2">
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${getStatusColor(invoiceData.status)}15`,
                      color: getStatusColor(invoiceData.status),
                    }}
                  >
                    {invoiceData.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-2" style={{ color: THEME.text }}>
                Client Information
              </h3>
              <div className="space-y-1">
                <p className="text-sm" style={{ color: THEME.text }}>
                  {clientData.client_name}
                </p>
                <p className="text-sm" style={{ color: THEME.subtext }}>
                  {clientData.client_email}
                </p>
                {clientData.client_phone && (
                  <p className="text-sm" style={{ color: THEME.subtext }}>
                    {clientData.client_phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Items Section */}
          {showLineItems && (
            <div className="bg-white rounded-xl p-6" data-section-name="invoice-items" style={{ boxShadow: shadows.lg }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
                Invoice Items
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      {showPrices && (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoiceItems.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: THEME.text }}>
                          {item.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: THEME.text }}>
                          {item.quantity}
                        </td>
                        {showPrices && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: THEME.text }}>
                              ${item.unit_price.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: THEME.text }}>
                              ${(item.quantity * item.unit_price).toFixed(2)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {showPrices && (
                <div className="mt-6 flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span style={{ color: THEME.subtext }}>Subtotal</span>
                      <span style={{ color: THEME.text }}>${invoiceData.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: THEME.subtext }}>Tax ({invoiceData.tax_rate}%)</span>
                      <span style={{ color: THEME.text }}>${invoiceData.tax_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold">
                      <span style={{ color: THEME.text }}>Total</span>
                      <span style={{ color: THEME.primary }}>${invoiceData.total_amount.toFixed(2)}</span>
                    </div>
                    {invoiceData.paid_amount > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span style={{ color: THEME.success }}>Paid</span>
                          <span style={{ color: THEME.success }}>${invoiceData.paid_amount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span style={{ color: THEME.text }}>Balance</span>
                          <span style={{ color: THEME.text }}>
                            ${(invoiceData.total_amount - invoiceData.paid_amount).toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes & Terms Section */}
          {(invoiceData.notes || invoiceData.terms) && (
            <div className="bg-white rounded-xl p-6" data-section-name="notes" style={{ boxShadow: shadows.lg }}>
              {invoiceData.notes && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: THEME.text }}>
                    Notes
                  </h3>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: THEME.subtext }}>
                    {invoiceData.notes}
                  </p>
                </div>
              )}
              {invoiceData.terms && (
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: THEME.text }}>
                    Terms & Conditions
                  </h3>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: THEME.subtext }}>
                    {invoiceData.terms}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 